import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Plus, Map, CalendarDays, Route, Link2 } from 'lucide-react';
import { fetchMyActivities, publishActivityRoute } from '../../api/move';
import type { MoveActivity } from '../../api/move';
import styles from '../../styles/Move.module.css';

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

function formatDistance(m: number) {
  if (m >= 1000) return `${(m / 1000).toFixed(1)} km`;
  return `${m} m`;
}

export default function MyActivityPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<MoveActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [publishingId, setPublishingId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  async function loadActivities() {
    try {
      const data = await fetchMyActivities();
      setItems(data);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Error cargando actividad');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadActivities(); }, []);

  function flashToast(message: string) {
    setToast(message);
    setTimeout(() => setToast(null), 2200);
  }

  async function handlePublishRoute(activity: MoveActivity) {
    setPublishingId(activity.activity_id);
    try {
      const result = await publishActivityRoute(activity.activity_id, {
        titulo: activity.route_titulo || undefined,
      });
      await loadActivities();
      navigate(`/move/routes/${result.route_id}`);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'No se pudo publicar la ruta');
    } finally {
      setPublishingId(null);
    }
  }

  const totalMeters = items.reduce((s, a) => s + a.distancia_m, 0);
  const totalMin = items.reduce((s, a) => s + a.duracion_min, 0);

  return (
    <div className={`move-app ${styles.page}`}>
      <header className={styles.hero}>
        <div className={styles.heroBg} />
        <div className={styles.heroOrb} />
        <div className={styles.heroInner}>
          <p className={styles.greetingLabel}>Tu progreso</p>
          <h1 className={styles.greetingName}>Mi actividad</h1>
        </div>
      </header>

      <section className={styles.quickStats}>
        <div className={styles.statCard}>
          <p className={styles.statValue}>{items.length}</p>
          <p className={styles.statLabel}>Sesiones</p>
        </div>
        <div className={styles.statCard}>
          <p className={styles.statValue}>{formatDistance(totalMeters)}</p>
          <p className={styles.statLabel}>Distancia total</p>
        </div>
        <div className={styles.statCard}>
          <p className={styles.statValue}>{Math.round(totalMin / 60)}h</p>
          <p className={styles.statLabel}>Tiempo total</p>
        </div>
      </section>

      <section className={styles.section}>
        <div style={{ display: 'grid', gap: 10 }}>
          <button className={styles.btnPrimary} style={{ width: '100%' }} onClick={() => navigate('/move/activity/new')}>
            <Plus size={16} /> Registrar actividad
          </button>
          <button
            className={styles.card}
            type="button"
            onClick={() => flashToast('Vincular con Strava en camino...')}
            style={{
              boxShadow: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: 'hsl(18 94% 54% / 0.08)',
              borderColor: 'hsl(18 94% 54% / 0.18)',
              width: '100%',
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            <div className={styles.goalIconBox} style={{ background: 'var(--move-grad-energy)' }}>
              <Link2 size={16} />
            </div>
            <div>
              <p className={styles.cardTitle} style={{ margin: 0 }}>Vincular con Strava en camino...</p>
              <p className={styles.cardSubtitle} style={{ marginTop: 4 }}>De momento registra tu progreso manualmente.</p>
            </div>
          </button>
        </div>
      </section>

      <section className={styles.section}>
        {loading && <div className={styles.empty}>Cargando…</div>}
        {error && <div className={styles.empty}>{error}</div>}
        <div className={styles.cardList}>
          {!loading && items.length === 0 && (
            <div className={styles.empty}>Aún no has registrado ninguna actividad</div>
          )}
          {items.map(a => (
            <div key={a.activity_id} className={styles.card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className={`${styles.iconBox} ${styles.primary}`} style={{ width: 44, height: 44 }}>
                  <Activity size={20} />
                </div>
                <div style={{ flex: 1 }}>
                  <p className={styles.cardTitle}>
                    {a.tipo} · {formatDistance(a.distancia_m)}
                  </p>
                  <p className={styles.cardSubtitle}>{formatDate(a.fecha)}</p>
                </div>
              </div>
              <div className={styles.cardMeta}>
                <span>{a.duracion_min} min</span>
                {a.desnivel_m > 0 && <span>+{a.desnivel_m} m</span>}
                {a.kcal_estimadas != null && <span>{a.kcal_estimadas} kcal</span>}
              </div>
              {a.route_titulo && (
                <div
                  onClick={() => navigate(`/move/routes/${a.route_id}`)}
                  style={{
                    marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 5,
                    fontSize: 12, color: 'var(--move-primary)', cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  <Map size={13} /> {a.route_titulo}
                </div>
              )}
              {a.event_titulo && (
                <div
                  onClick={() => navigate(`/move/events/${a.event_id}`)}
                  style={{
                    marginTop: a.route_titulo ? 4 : 8,
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    fontSize: 12, color: 'hsl(18 94% 54%)', cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  <CalendarDays size={13} /> {a.event_titulo}
                </div>
              )}
              {a.notas && <p style={{ marginTop: 8, fontSize: 13, color: 'var(--move-muted-fg)' }}>{a.notas}</p>}
              {a.has_route_geometry && !a.route_id && (
                <button
                  className={styles.btnGhost}
                  type="button"
                  disabled={publishingId === a.activity_id}
                  onClick={() => handlePublishRoute(a)}
                  style={{ width: '100%', marginTop: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  <Route size={15} /> {publishingId === a.activity_id ? 'Publicando…' : 'Registrar esta ruta para todos'}
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      {toast && (
        <div style={{
          position: 'fixed',
          bottom: 100,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'var(--move-secondary)',
          color: '#fff',
          padding: '10px 18px',
          borderRadius: 999,
          fontSize: 13,
          fontWeight: 600,
          boxShadow: 'var(--move-shadow-md)',
          zIndex: 100,
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}
